import type { Action, ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import storageSession from 'redux-persist/lib/storage/session';
import {
  persistStore,
  persistReducer,
  REHYDRATE,
  PAUSE,
  FLUSH,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import * as Sentry from '@sentry/react';

import { clearTourDismissed } from 'pages/dashboard/tour/tourDismissedStore';
// slices
import apiSlice from 'services/api/apiSlice';
import alertsSlice from 'services/alerts/alertsSlice';
import userSlice from 'services/user/userSlice';
import sessionSlice from 'services/session/sessionSlice';
import watchlistSlice from 'services/watchlist/watchlistSlice';
import enrollmentSlice from 'services/enrollment/enrollmentSlice';
import offerSlice from 'services/offer/offerSlice';
import dbooSlice from 'services/dboo/dbooSlice';
import vaultSlice from 'services/vault/vaultSlice';
import brandSlice from 'services/brand/brandSlice';
import toastAlertSlice from 'services/toast-alert/toastAlertSlice';
import planSlice from 'services/plan/planSlice';
import criticalNotificationsSlice from 'services/misc/criticalNotificationSlice';
import miscSlice from 'services/misc/miscSlice';
import secureUploadSlice from 'services/secureUpload/secureUploadSlice';
import securitySlice from 'services/security/securitySlice';
import ctpSlice from 'services/ctp/ctpSlice';
import htmonSlice from 'services/htmon/htmonSlice';
import publicRecordsSlice from 'services/public-records/publicRecordsSlice';
import creditScoreSlice from 'services/credit-score/creditScoreSlice';
import creditScoreSimulatorSlice from 'pages/credit-score-simulator/services/creditScoreSimulatorSlice';
import creditLockSlice from 'services/credit-lock/creditLockSlice';
import notificationSlice from 'services/notification/notificationSlice';
import notificationSettingsSlice from 'services/notification-settings/notificationSettingsSlice';
import tuidVerifySlice from 'modules/tuid-verify/services/tuidVerifySlice';
import idauthVerifySlice from 'modules/idauth-verify/services/idauthVerifySlice';
import reviewContactDetailsSlice from 'modules/review-contact-details/services/reviewContactDetailsSlice';
import creditReportThreeBSlice from 'services/credit-report/three-b/creditReportThreeBSlice';
import creditReportTbcrSlice from 'services/credit-report/tbcr/creditReportTbcrSlice';
import creditReportCrtbSlice from 'services/credit-report/crtb/creditReportCrtbSlice';
import creditReportOneBureauSlice from 'services/credit-report/one-bureau/creditReportOneBureauSlice';
import userFailReportSlice from 'services/user-fail-report/userFailReportSlice';
import txmonItemsSlice from 'modules/transaction-monitoring/txmonItemsSlice';
import txmonTransactionsSlice from 'modules/transaction-monitoring/txmonTransactionsSlice';
import socialMediaSlice from 'modules/social-media-monitoring/socialMediaSlice';
import invitorSlice from 'services/invitor/invitorSlice';
import childItemsSlice from 'services/child-items/childItemsSlice';
import securityFreezeSlice from 'services/security-freeze/securityFreezeSlice';
import spendingSlice from 'services/spending/spendingSlice';
import healthlockSlice from 'services/healthlock/healthlockSlice';
import insightsAndTipsSlice from 'services/insights-and-tips/insightsAndTipsSlice';
import marketingSlice from 'services/marketing/marketingSlice';
import ssntSlice from 'services/ssnt/ssntSlice';

/**
 * Enrollment survives a mid-flow refresh (offer codes, redeem code, prospect token,
 * consents) but the account credentials never reach storage — identityWeb likewise keeps
 * them in an in-memory service and nulls the password out of its persisted slice. The
 * cost is that a refresh on /enrollment/creating-dashboard cannot auto-sign-in; that screen
 * falls back to sending the member to the sign-in page.
 */
const enrollmentPersistConfig = {
  key: 'enrollment',
  storage: storageSession,
  version: 1,
  blacklist: ['account'],
};

export const persistConfig = {
  key: 'root',
  storage: storageSession,
  version: 1,
  whitelist: [
    'session',
    'user',
    'alerts',
    // 'watchlist',
    'enrollment',
    'offer',
    /* Attribution is only present in the landing URL; a mid-flow refresh drops the query
       string, so it has to survive one for the account and payment steps to still report it. */
    'marketing',
    // 'dboo',
    'brand',
    'criticalNotifications',
    'misc',
    'ctp',
    'plan',
    'htmon',
    'publicRecords',
    'creditScore',
    'notification',
    /* Kept so a reload does not re-read the preferences endpoint: the mount fetch is guarded on
       the slice's own loaded gate, and every activate/deactivate re-reads it anyway. */
    'notificationSettings',
    'creditLock',
    'securityFreeze',
    'socialMedia',
  ], // persisted reducers
};

const appReducer = combineReducers({
  enrollment: persistReducer(enrollmentPersistConfig, enrollmentSlice),
  api: apiSlice,
  alerts: alertsSlice,
  user: userSlice,
  session: sessionSlice,
  watchlist: watchlistSlice,
  offer: offerSlice,
  plan: planSlice,
  dboo: dbooSlice,
  vault: vaultSlice,
  brand: brandSlice,
  toastAlert: toastAlertSlice,
  criticalNotifications: criticalNotificationsSlice,
  misc: miscSlice,
  secureUpload: secureUploadSlice,
  security: securitySlice,
  ctp: ctpSlice,
  htmon: htmonSlice,
  publicRecords: publicRecordsSlice,
  creditScore: creditScoreSlice,
  creditScoreSimulator: creditScoreSimulatorSlice,
  creditLock: creditLockSlice,
  notification: notificationSlice,
  /* Deliberately NOT in the persist whitelist above: these are settings the member mutates,
     so a persisted copy would serve a stale switch state across a reload. The fetch-once guard
     (FR-004) rides on the in-memory `channels !== null` sentinel, which survives in-app
     navigation without persistence. */
  notificationSettings: notificationSettingsSlice,
  tuidVerify: tuidVerifySlice,
  idauthVerify: idauthVerifySlice,
  reviewContactDetails: reviewContactDetailsSlice,
  creditReportThreeB: creditReportThreeBSlice,
  creditReportTbcr: creditReportTbcrSlice,
  creditReportCrtb: creditReportCrtbSlice,
  creditReportOneBureau: creditReportOneBureauSlice,
  userFailReport: userFailReportSlice,
  txmonItems: txmonItemsSlice,
  txmonTransactions: txmonTransactionsSlice,
  socialMedia: socialMediaSlice,
  invitor: invitorSlice,
  childItems: childItemsSlice,
  securityFreeze: securityFreezeSlice,
  spending: spendingSlice,
  healthlock: healthlockSlice,
  insightsAndTips: insightsAndTipsSlice,
  marketing: marketingSlice,
  // Deliberately NOT in the persist whitelist above: each page open refetches the
  // report, so a persisted copy would only ever serve stale data across reloads.
  ssnt: ssntSlice,
});

const sentryReduxEnhancer = Sentry.createReduxEnhancer({});

const rootReducer = (state: any, action: UnknownAction) => {
  if (action.type === 'SIGNOUT_REQUEST') {
    storageSession.removeItem('persist:root');
    sessionStorage.removeItem('contactPopupShown');
    /* Re-arms the dashboard onboarding tour's session guard, mirroring Angular's
       `delete-session.service.ts`. Goes through the store rather than removing
       the key directly, so the in-memory snapshot the gate subscribes to is
       reset too — the returning user is then filtered by the server-side
       `skipTourCount` / `dontShowTour` attributes instead. */
    clearTourDismissed();
    // reducers are supposed to return the initial state when they are called with undefined as the first argument, no matter the action.
    return appReducer(undefined, action);
  }

  return appReducer(state, action);
};

// Infer the `RootState` type from the root reducer
export type RootState = ReturnType<typeof rootReducer>;

const persistedReducer = persistReducer(persistConfig, rootReducer);

// The store setup is wrapped in `makeStore` to allow reuse
// when setting up tests that need the same store config
export const makeStore = () => {
  const store = configureStore({
    reducer: persistedReducer,
    devTools: import.meta.env.VITE_PS_ENV !== 'prod',
    // Adding the api middleware enables caching, invalidation, polling,
    // and other useful features of `rtk-query`.
    // middleware: (getDefaultMiddleware) => {
    //   return getDefaultMiddleware().concat(quotesApiSlice.middleware);
    // },
    // preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }),
    enhancers: (getDefaultEnhancers) => {
      return getDefaultEnhancers().concat(sentryReduxEnhancer);
    },
  });
  // configure listeners using the provided defaults
  // optional, but required for `refetchOnFocus`/`refetchOnReconnect` behaviors
  return store;
};

export const store = makeStore();

export const persistor = persistStore(store);

// Infer the type of `store`
export type AppStore = typeof store;
// Infer the `AppDispatch` type from the store itself
export type AppDispatch = AppStore['dispatch'];
export type AppState = AppStore['getState'];
export type AppThunk<ThunkReturnType = void> = ThunkAction<
  ThunkReturnType,
  RootState,
  unknown,
  Action
>;
